import React from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { GoHome } from "react-icons/go";
import { FaSpotify, FaRocket, FaTrophy, FaClock, FaChalkboardTeacher, FaUsers, FaLaptopCode, FaQuestionCircle } from "react-icons/fa";
import { VscLibrary } from "react-icons/vsc";
import { BsHeartFill, BsList, BsPinAngleFill } from "react-icons/bs";
import './Sidebar.css';

const Sidebar = ({ onNavigate }) => {
    const location = useLocation();

    // Check if we're on a tutoring-related page
    const isTutoringPage = ['/tutoring', '/book-time', '/thank-you'].some(
        path => location.pathname.startsWith(path)
    );

    // Library items for portfolio pages
    const portfolioItems = [
        {
            id: 1,
            name: 'Liked Songs',
            type: 'Playlist',
            isLikedSongs: true,
            path: '/',
            isPinned: true
        },
        {
            id: 2,
            name: 'Projects',
            type: 'Playlist',
            path: '/projects'
        },
        {
            id: 3,
            name: 'Experience',
            type: 'Playlist',
            path: '/experience'
        },
        {
            id: 4,
            name: 'Education',
            type: 'Playlist',
            path: '/education'
        },
        {
            id: 5,
            name: 'Blog',
            type: 'Playlist',
            path: '/blog'
        },
        {
            id: 6,
            name: 'My Whip',
            type: 'Playlist',
            path: '/whip'
        },
    ];

    // Library items for tutoring pages (anchor links to sections)
    const tutoringItems = [
        {
            id: 1,
            name: 'Tutoring Home',
            type: 'Overview',
            icon: <FaChalkboardTeacher />,
            path: '/tutoring',
            isAnchor: false,
            isPinned: true
        },
        {
            id: 2,
            name: 'Who Is This For',
            type: 'Audience',
            icon: <FaUsers />,
            anchor: '#audience',
            isAnchor: true
        },
        {
            id: 3,
            name: 'Curriculum',
            type: 'Topics',
            icon: <FaLaptopCode />,
            anchor: '#curriculum',
            isAnchor: true
        },
        {
            id: 4,
            name: 'Programs',
            type: '10-Week Courses',
            icon: <FaRocket />,
            anchor: '#programs',
            isAnchor: true
        },
        {
            id: 5,
            name: 'Hourly Tutoring',
            type: '$32/hour',
            icon: <FaClock />,
            anchor: '#hourly',
            isAnchor: true
        },
        {
            id: 6,
            name: 'Success Stories',
            type: 'Ivy League',
            icon: <FaTrophy />,
            anchor: '#success',
            isAnchor: true
        },
        {
            id: 7,
            name: 'FAQ',
            type: 'Questions',
            icon: <FaQuestionCircle />,
            anchor: '#faq',
            isAnchor: true
        },
    ];

    const libraryItems = isTutoringPage ? tutoringItems : portfolioItems;

    const handleItemClick = (item, e) => {
        if (item.isAnchor && item.anchor) {
            e.preventDefault();
            const element = document.querySelector(item.anchor);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
        onNavigate && onNavigate();
    };

    return (
        <div className="sidebar">
            {/* Top Section - Logo + Home on same row */}
            <div className="sidebar-top">
                <div className="sidebar-top-row">
                    <Link to="/" className="sidebar-logo">
                        <FaSpotify className="logo-icon" />
                    </Link>
                    <NavLink to="/" end className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} onClick={onNavigate}>
                        <GoHome className="sidebar-icon" />
                        <span>Home</span>
                    </NavLink>
                </div>
            </div>

            {/* Library Section */}
            <div className="sidebar-library">
                <div className="library-header">
                    <div className="library-header-left">
                        <VscLibrary size={24} />
                        <span>{isTutoringPage ? 'Tutoring' : 'Your Library'}</span>
                    </div>
                </div>

                {/* Playlists filter + Recents on same row */}
                <div className="library-filter-row">
                    <button className="filter-pill active">
                        {isTutoringPage ? 'Sections' : 'Playlists'}
                    </button>
                    <div className="library-sort">
                        <span>{isTutoringPage ? 'Jump to' : 'Recents'}</span>
                        <BsList size={16} />
                    </div>
                </div>

                <div className="playlist-list">
                    {libraryItems.map(item => (
                        item.isAnchor ? (
                            <a
                                href={item.anchor}
                                key={item.id}
                                className="playlist-item"
                                onClick={(e) => handleItemClick(item, e)}
                            >
                                <div className="playlist-item-art tutoring-icon">
                                    {item.icon}
                                </div>
                                <div className="playlist-item-info">
                                    <div className="playlist-item-name">{item.name}</div>
                                    <div className="playlist-item-meta">
                                        <span>{item.type}</span>
                                    </div>
                                </div>
                            </a>
                        ) : (
                            <NavLink
                                to={item.path}
                                key={item.id}
                                className={({ isActive }) => `playlist-item ${isActive ? 'active' : ''}`}
                                onClick={(e) => handleItemClick(item, e)}
                            >
                                <div className={`playlist-item-art ${item.isLikedSongs ? 'liked-songs' : ''} ${item.icon ? 'tutoring-icon' : ''}`}>
                                    {item.isLikedSongs ? (
                                        <BsHeartFill size={16} color="white" />
                                    ) : item.icon ? (
                                        item.icon
                                    ) : (
                                        <span className="playlist-art-placeholder">{item.name.charAt(0)}</span>
                                    )}
                                </div>
                                <div className="playlist-item-info">
                                    <div className="playlist-item-name">{item.name}</div>
                                    <div className="playlist-item-meta">
                                        {item.isPinned && <BsPinAngleFill size={10} className="text-green" style={{ marginRight: 4 }} />}
                                        <span>{item.type}</span>
                                    </div>
                                </div>
                            </NavLink>
                        )
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
